/**
 * Authenticated fetch for bioapp-v2.
 *
 * The session lives in httpOnly cookies set by the fleet's `/api/callback`, so JavaScript never
 * sees a token - it only has to make sure every request carries credentials. Use this rather than
 * raw `fetch` so that happens by default, and so a 401 gets one refresh attempt before the caller
 * concludes the session is gone.
 *
 * Ported from bioapp v1, whose two non-obvious behaviours are kept deliberately; see below.
 */

/** The fleet serves this app and its API from one origin, so every path is relative. */
export const API_PREFIX = "/api";

export const HTTP_UNAUTHORIZED = 401;
export const HTTP_FORBIDDEN = 403;
export const HTTP_NOT_FOUND = 404;
export const HTTP_TOO_MANY_REQUESTS = 429;

/**
 * Why a refresh did not produce a usable session.
 *
 * `throttled` is not `failed`: the server said "ask again later", not "you are signed out".
 * Collapsing the two signs the user out whenever their own traffic trips the rate limit, which is
 * the worst possible moment to end a session.
 */
export type RefreshOutcome = "refreshed" | "throttled" | "failed";

/**
 * The refresh currently in flight, if any. Doubles as the single-flight lock.
 *
 * One variable rather than a separate boolean flag: with two, any path that sets the flag and then
 * fails before assigning the promise leaves them disagreeing, and every later refresh returns a
 * null promise instead of making a request. The page then signs the user out at the first expiry,
 * for the rest of its life, with nothing in the console.
 */
let refreshInFlight: Promise<RefreshOutcome> | null = null;

async function refreshOnce(): Promise<RefreshOutcome> {
  try {
    const response = await fetch(`${API_PREFIX}/refresh`, { method: "POST", credentials: "include" });
    if (response.ok) return "refreshed";
    return response.status === HTTP_TOO_MANY_REQUESTS ? "throttled" : "failed";
  } catch {
    // A network error is not evidence the session ended either, so it is worth retrying rather
    // than signing the user out.
    return "throttled";
  }
}

function tryRefresh(): Promise<RefreshOutcome> {
  refreshInFlight ??= refreshOnce().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

export interface ApiFetchOptions extends RequestInit {
  /** Set on the refresh call itself, so a failing refresh cannot recurse. */
  skipRefresh?: boolean;
}

/**
 * Fetch with the session cookie attached, retrying once after a successful token refresh.
 *
 * A refresh that is itself rate limited surfaces as 429 rather than the original 401: callers read
 * 401 as "the session is gone" and sign out, so passing it through would turn a burst of the
 * user's own traffic into a logout.
 */
export async function apiFetch(path: string, options: ApiFetchOptions = {}): Promise<Response> {
  const { skipRefresh, ...init } = options;
  const response = await fetch(path, { ...init, credentials: "include" });

  if (response.status !== HTTP_UNAUTHORIZED || skipRefresh) {
    return response;
  }

  const outcome = await tryRefresh();
  if (outcome === "refreshed") {
    return fetch(path, { ...init, credentials: "include" });
  }
  if (outcome === "throttled") {
    return Response.json(
      { success: false, data: null, errors: ["Too Many Requests: could not refresh the session"] },
      { status: HTTP_TOO_MANY_REQUESTS },
    );
  }
  return response;
}

/** Every worker route is POST, including reads - see ilworkers/api_helper.py. */
export async function apiPost(endpoint: string, body?: unknown): Promise<Response> {
  return apiFetch(`${API_PREFIX}/${endpoint}`, {
    method: "POST",
    headers: body === undefined ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

/** The fleet's standard response envelope. */
export interface ApiResponse<T> {
  success: boolean;
  data: T | null;
  errors: string[];
}

/** POST an endpoint and unwrap the envelope, throwing on anything that is not a success. */
export async function apiPostJson<T>(endpoint: string, body?: unknown): Promise<T> {
  const response = await apiPost(endpoint, body);
  if (!response.ok) {
    throw new Error(`${endpoint} returned HTTP ${response.status}`);
  }
  const payload = (await response.json()) as ApiResponse<T>;
  if (!payload.success) {
    throw new Error(payload.errors.join("; ") || `${endpoint} reported failure`);
  }
  return payload.data as T;
}
